import { createColumnHelper } from "@tanstack/react-table";

import { conversionRateTableColumnKeys, tableColumns } from "../../constants";
import { compactNumberFormat, compareNumericString } from "../../../../shared";

const {
  BRAND_NAME,
  SURVEY_SENT_COUNT,
  GOOGLE_REVIEW_COUNT,
  SURVEY_OPENED_COUNT,
  SURVEY_STARTED_COUNT,
  SURVEY_COMPLETE_COUNT,
  PASSIVE_SURVEY_COMPLETED_COUNT,
  NEGATIVE_SURVEY_COMPLETED_COUNT,
  POSITIVE_SURVEY_COMPLETED_COUNT,
} = conversionRateTableColumnKeys;

//Function to sort table data with value only.
const numericSortingFn = (key) => (a, b) => {
  return a.original[key].value - b.original[key].value;
};

const columnHelper = createColumnHelper();

export const getConversionRateTableColumns = (
  isGoogleReviewFilterSelected,
  cellNameRenderer
) => {
  const Columns = [
    columnHelper.accessor(BRAND_NAME, {
      header: () => "Brand Name",
      cell: cellNameRenderer,
    }),
    columnHelper.accessor(SURVEY_SENT_COUNT, {
      header: () => "Surveys Sent",
      cell: (info) => {
        const { value } = info.getValue();
        return compactNumberFormat(value);
      },
    }),
    columnHelper.accessor(SURVEY_OPENED_COUNT, {
      header: () => "Survey Opened",
      cell: (info) => {
        const { value, percentage } = info.getValue();
        return `${compactNumberFormat(value)} (${percentage}%)`;
      },
      sortingFn: numericSortingFn(SURVEY_OPENED_COUNT),
    }),
    columnHelper.accessor(SURVEY_STARTED_COUNT, {
      header: () => "Surveys Started",
      cell: (info) => {
        const { value, percentage } = info.getValue();
        return `${compactNumberFormat(value)} (${percentage}%)`;
      },
      sortingFn: numericSortingFn(SURVEY_STARTED_COUNT),
    }),
    columnHelper.accessor(SURVEY_COMPLETE_COUNT, {
      header: () => "Surveys Complete",
      cell: (info) => {
        const { value, percentage } = info.getValue();
        return `${compactNumberFormat(value)} (${percentage}%)`;
      },
      sortingFn: numericSortingFn(SURVEY_COMPLETE_COUNT),
    }),
    columnHelper.accessor(POSITIVE_SURVEY_COMPLETED_COUNT, {
      header: () => "Positive",
      cell: (info) => {
        const { value, percentage } = info.getValue();
        return `${compactNumberFormat(value)} (${percentage}%)`;
      },
      sortingFn: numericSortingFn(POSITIVE_SURVEY_COMPLETED_COUNT),
    }),
    columnHelper.accessor(PASSIVE_SURVEY_COMPLETED_COUNT, {
      header: () => "Passive",
      cell: (info) => {
        const { value, percentage } = info.getValue();
        return `${compactNumberFormat(value)} (${percentage}%)`;
      },
      sortingFn: numericSortingFn(PASSIVE_SURVEY_COMPLETED_COUNT),
    }),
    columnHelper.accessor(NEGATIVE_SURVEY_COMPLETED_COUNT, {
      header: () => "Negative",
      cell: (info) => {
        const { value, percentage } = info.getValue();
        return `${compactNumberFormat(value)} (${percentage}%)`;
      },
      sortingFn: numericSortingFn(NEGATIVE_SURVEY_COMPLETED_COUNT),
    }),
    ...(isGoogleReviewFilterSelected()
      ? [
          columnHelper.accessor(GOOGLE_REVIEW_COUNT, {
            header: () => "Redirect to Google Review",
            cell: (info) => {
              const { value, percentage } = info.getValue();
              return `${compactNumberFormat(value)} (${percentage}%)`;
            },
            sortingFn: numericSortingFn(GOOGLE_REVIEW_COUNT),
          }),
        ]
      : []),
  ];
  return Columns;
};

export const getStarRatingTableColumns = (
  cellNameRenderer,
  cellChangeRenderer,
  headerSurveyCompletionDateRenderer
) => {
  const columns = [
    columnHelper.accessor("name", {
      header: () => "BRAND NAME",
      cell: cellNameRenderer,
    }),
    columnHelper.accessor("recordCount", {
      header: () => "TOTAL RESPONSES",
    }),
    columnHelper.accessor("averageGuestRatings", {
      header: () => "GUEST RATING",
    }),
    columnHelper.accessor("previousGuestRatings", {
      header: headerSurveyCompletionDateRenderer,
      minSize: tableColumns.SIZE_200,
      enableSorting: false,
      cell: (row) => row.getValue() || "-",
    }),
    columnHelper.accessor("change", {
      header: () => "CHANGE",
      enableSorting: true,
      cell: cellChangeRenderer,
      sortingFn: compareNumericString,
    }),
  ];
  return columns;
};

export const getInboxReportTableColumns = (cellNameRenderer, isStore) => {
  return [
    ...(isStore
      ? [
          {
            //This for icon column space, so that store name is aligned with brand
            name: <span className="fw-bold">STORE</span>,
            selector: (row) => {
              return row.test;
            },
            width: `3rem`,
          },
          {
            name: <span className="fw-bold">STORE</span>,
            selector: (row) => {
              return row.storeName;
            },
            width: `11.5rem`,
            cell: cellNameRenderer,
          },
        ]
      : [
          {
            name: <span className="fw-bold">BRAND</span>,
            selector: (row) => {
              return row.brandName;
            },
            width: `11.5rem`,
            cell: cellNameRenderer,
          },
        ]),
    {
      name: <span className="fw-bold">CONVERSATION (TOTAL)</span>,
      selector: (row) => row.totalConversation,
      width: tableColumns.SIZE_8REM,
      cell: (row) => compactNumberFormat(row.totalConversation),
    },
    {
      name: <span className="fw-bold">CONVERSATION (NOT STARTED)</span>,
      selector: (row) => row.conversationNotStarted,
      width: tableColumns.SIZE_8REM,
      cell: (row) => compactNumberFormat(row.conversationNotStarted),
    },
    {
      name: <span className="fw-bold">CONVERSATION (STARTED)</span>,
      selector: (row) => row.conversationStarted,
      width: tableColumns.SIZE_8REM,
      cell: (row) => compactNumberFormat(row.conversationStarted),
    },
    {
      name: <span className="fw-bold">STARTED RATE</span>,
      selector: (row) => row.conversationStartedRate,
      width: tableColumns.SIZE_8REM,
      cell: (row) => `${row.conversationStartedRate}%`,
    },
    {
      name: <span className="fw-bold">CONSUMER (DID NOT RESPOND)</span>,
      selector: (row) => row.consumerNotResponded,
      cell: (row) => compactNumberFormat(row.consumerNotResponded),
    },
    {
      name: <span className="fw-bold">CONSUMER (RESPONDED)</span>,
      selector: (row) => row.consumerResponded,
      cell: (row) => compactNumberFormat(row.consumerResponded),
    },
    {
      name: <span className="fw-bold">RESPONSE RATE</span>,
      selector: (row) => row.consumerResponseRate,
      width: tableColumns.SIZE_100,
      cell: (row) => `${row.consumerResponseRate}%`,
    },
  ];
};
